import { type ReactElement, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { SocialLinkConfirm } from '@/features/social-link';
import { SocialSignupForm } from '@/features/social-signup';
import { useSession } from '@/entities/session';
import { useSessionRefresh } from '@/shared/api';
import { rememberLastSignin } from '@/shared/lib/last-signin';
import { signinLanding } from '@/shared/lib/signin-landing';
import {
  clearSocialHandoff,
  readSocialHandoff,
  type SocialProvider,
} from '@/shared/lib/social-handoff';

import { type CallbackStatus, toCallbackStatus } from '../model/callback-status';
import { CallbackNotice, type CallbackNoticeProps } from './callback-notice';

/** 돌아갈 곳을 모르면 1차 오픈의 착지 지점으로 보낸다. */
const DEFAULT_NEXT = '/library';

/**
 * 사용자가 더 할 일이 없는 갈래의 안내.
 *
 * `signedIn`·`signupRequired`·`linkRequired` 는 여기 없다 — 그 셋은 보여줄 안내가 아니라
 * 할 일(재발급·약관 동의·연결 확인)이 있다.
 */
const NOTICES: Record<
  Exclude<CallbackStatus, 'signedIn' | 'signupRequired' | 'linkRequired'>,
  CallbackNoticeProps
> = {
  blocked: {
    title: '이용이 제한된 계정입니다',
    description: '문의가 필요하시면 고객센터로 연락해 주세요.',
    action: { to: '/library', label: '자료실로 가기' },
  },
  withdrawn: {
    title: '탈퇴한 계정입니다',
    description: '같은 계정으로 다시 가입하실 수 있습니다.',
    action: { to: '/signup', label: '가입 화면으로' },
  },
  emailNotVerified: {
    title: '이메일 인증이 필요합니다',
    description: '가입할 때 보내 드린 인증 메일의 링크를 눌러 주세요.',
    action: { to: '/login', label: '로그인 화면으로' },
  },
  emailRequired: {
    title: '이메일을 받지 못했습니다',
    description: '제공사 계정에 이메일을 등록·인증한 뒤 다시 시도해 주세요.',
    action: { to: '/login', label: '다시 시도하기' },
  },
  failed: {
    title: '로그인하지 못했습니다',
    description: '잠시 후 다시 시도해 주세요.',
    action: { to: '/login', label: '다시 시도하기' },
  },
};

/**
 * 소셜 로그인이 끝나고 브라우저가 되돌아오는 자리 (`/auth/callback?status=...`).
 *
 * 서버는 토큰도 티켓도 주소창에 싣지 않는다 — 쿠키로 보내고, 여기에는 코드값 하나만 온다.
 * 그 값은 **무엇을 보여줄지만** 정한다. 주소창은 사용자가 고칠 수 있으므로
 * `?status=signedIn` 을 손으로 넣어도 실제 로그인은 `auth.refresh` 가 성공해야 이뤄진다.
 */
export function AuthCallbackPage(): ReactElement {
  const [params] = useSearchParams();
  const status = toCallbackStatus(params.get('status'));
  // 세 갈래(로그인·가입·연결) 모두 끝나는 자리에서 쪽지를 지우므로 여기서는 한 번만 읽는다.
  const [handoff] = useState(readSocialHandoff);
  const next = handoff?.next ?? DEFAULT_NEXT;

  const provider = handoff?.provider ?? null;

  if (status === 'signedIn') {
    return <SignedInLanding next={next} provider={provider} />;
  }

  if (status === 'signupRequired') {
    return <SocialSignupForm provider={provider} next={next} />;
  }

  // 같은 이메일의 회원이 이미 있다. 자동으로 잇지 않고 본인 확인을 받는다 (ADR-013).
  if (status === 'linkRequired') {
    return <SocialLinkConfirm provider={provider} next={next} />;
  }

  return <CallbackNotice {...NOTICES[status]} />;
}

/**
 * 쿠키에 실려 온 리프레시 토큰으로 세션을 받아 원래 가려던 곳으로 보낸다.
 *
 * 재발급은 앱 전역의 재발급기로만 부른다 — 부팅 복구와 동시에 걸려도 한 번으로 합쳐진다.
 * 따로 부르면 회전한 토큰이 두 번 쓰이고, 서버는 그것을 탈취로 보고 세션을 끊는다.
 */
function SignedInLanding({
  next,
  provider,
}: {
  next: string;
  provider: SocialProvider | null;
}): ReactElement {
  const refresh = useSessionRefresh();
  const navigate = useNavigate();
  const signIn = useSession((state) => state.signIn);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    void refresh().then(
      (member) => {
        if (isCancelled) return;
        // 스토어를 먼저 채운다 — 순서가 바뀌면 도착한 화면이 잠깐 비로그인으로 그려진다.
        signIn(member);
        // 다음에 이 사람이 로그인 화면에서 어느 문으로 들어왔는지 알아볼 수 있게 남긴다.
        if (provider !== null) {
          rememberLastSignin(provider);
        }
        clearSocialHandoff();
        void navigate(signinLanding(next, member.onboarding.isCompleted), { replace: true });
      },
      () => {
        if (!isCancelled) setHasFailed(true);
      },
    );

    return () => {
      isCancelled = true;
    };
  }, [refresh, signIn, navigate, next, provider]);

  if (hasFailed) {
    return <CallbackNotice {...NOTICES.failed} />;
  }

  return (
    <div className="u-container flex justify-center py-24">
      <p role="status" className="text-[14px] text-muted-foreground">
        로그인 중입니다…
      </p>
    </div>
  );
}
